// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DetailPageBody } from '@/components/detail-page-body';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { HorizontalNavigationLink } from '@/components/horizontal-navigation-link';
import { Warning } from '@/constants/hardfin-standard-icons';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { fulfillmentsQueryKey } from '@/fulfillment/query-keys';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Shipment, getShipmentType } from '@/shipment/api/types';
import { updateShipment } from '@/shipment/api/update-shipment';
import { DeliveryDialog } from '@/shipment/components/delivery-dialog';
import { InboundShipmentDialog } from '@/shipment/components/inbound-shipment-dialog';
import { OutboundShipDialog } from '@/shipment/components/outbound-ship-dialog';
import { OutboundShipmentDialog } from '@/shipment/components/outbound-shipment-dialog';
import { QuarantinePanel } from '@/shipment/components/quarantine-panel';
import { ReceiveDialog } from '@/shipment/components/receive-dialog';
import { ShipmentAuditCard } from '@/shipment/components/shipment-audit-card';
import { ShipmentCancelDialog } from '@/shipment/components/shipment-cancel-dialog';
import { ShipmentCarrierTracking } from '@/shipment/components/shipment-carrier-tracking';
import { ShipmentDetailHeader } from '@/shipment/components/shipment-detail-header';
import { ShipmentDetailLineItemsTable } from '@/shipment/components/shipment-detail-line-items-table';
import { ShipmentLocationInfo } from '@/shipment/components/shipment-location-info';
import { ShipmentNotesControl } from '@/shipment/components/shipment-notes-control';
import { ShipmentPhaseIndicator } from '@/shipment/components/shipment-phase-indicator';
import { shipmentDetailQueryKey } from '@/shipment/hooks/use-shipment';
import { useShipmentThirdPartySerialWarningPhases } from '@/shipment/hooks/use-shipment-third-party-serial-warning';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import {
  Box,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DateTime } from 'luxon';

const routeApi = getRouteApi('/_app/shipments/$shipmentKey');

interface Props {
  shipment: Shipment;
  shipmentKey: string;
}

/**
 * Detail page content for shipments.
 * Handles both inbound and outbound shipment types with appropriate dialogs and actions.
 */
export function ShipmentDetail(props: Props) {
  const queryClient = useQueryClient();

  const shipmentType = getShipmentType(props.shipment);
  const isInbound = shipmentType === 'inbound';
  const isCancelled = props.shipment.status === 'cancelled';
  const thirdPartySerialWarningPhases =
    useShipmentThirdPartySerialWarningPhases(props.shipment);

  const routeSearch = routeApi.useSearch();
  const holds = props.shipment.quarantinedSerials;
  const hasHolds = holds.length > 0;
  const openHoldCount = holds.filter((held) => held.resolution === null).length;
  const activeTab =
    routeSearch.tab === 'quarantine' && hasHolds ? 'quarantine' : 'line-items';

  // Dialog states using useDisclosure
  const editDialog = useDisclosure();
  const shipDialog = useDisclosure();
  const deliveryDialog = useDisclosure();
  const receiveDialog = useDisclosure();
  const cancelDialog = useDisclosure();

  // Handler for successful cancellation
  const handleCancelSuccess = (updatedShipment: Shipment) => {
    queryClient.setQueryData(
      shipmentDetailQueryKey(props.shipmentKey),
      updatedShipment
    );
    queryClient.invalidateQueries({
      queryKey: shipmentsQueryKey,
      refetchType: 'active',
    });
  };

  // Handler for editing shipped date via inline popover
  const handleEditShippedDate = async (newDate: DateTime) => {
    // Convert to UTC before serializing to ensure backend receives proper UTC timestamp
    const timezone = newDate.zoneName;
    await updateShipment(props.shipmentKey, {
      shippedAt: newDate.toUTC().toISO(),
      shippedAtTimezone: timezone,
    });
    queryClient.invalidateQueries({
      queryKey: shipmentDetailQueryKey(props.shipmentKey),
    });
    queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
    queryClient.invalidateQueries({ queryKey: fulfillmentsQueryKey });
  };

  return (
    <Container maxWidth="xl">
      <ShipmentDetailHeader
        shipment={props.shipment}
        shipmentType={shipmentType}
        onEditShipment={editDialog.open}
        onMarkShipped={isInbound ? undefined : shipDialog.open}
        onMarkDelivered={deliveryDialog.open}
        onMarkReceived={isInbound ? receiveDialog.open : undefined}
        onCancel={cancelDialog.open}
      />

      {/* Main content */}
      <DetailPageBody
        side={
          <>
            {/* Tracking section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ShipmentCarrierTracking shipment={props.shipment} />
            </Box>

            {/* Notes section */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <HardfinTabCardWrapper title="Notes" hideDivider>
                <ShipmentNotesControl shipment={props.shipment} />
              </HardfinTabCardWrapper>
            </Box>

            {/* Audit log */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ShipmentAuditCard shipmentKey={props.shipmentKey} />
            </Box>
          </>
        }
      >
        {/* Phase indicator and shipment info */}
        <Paper sx={{ minWidth: 0, padding: 3 }} variant="outlined">
          <ShipmentPhaseIndicator
            currentStatus={props.shipment.status}
            shipmentType={shipmentType}
            cancelledReason={props.shipment.cancellationReason}
            createdAt={props.shipment.createdAt}
            shippedAt={props.shipment.shippedAt}
            deliveredAt={props.shipment.deliveredAt}
            receivedAt={props.shipment.receivedAt}
            thirdPartySerialWarningPhases={thirdPartySerialWarningPhases}
            onEditShippedDate={
              isInbound && props.shipment.shippedAt && !isCancelled
                ? handleEditShippedDate
                : undefined
            }
            onEditShipConfirmation={
              !isInbound && props.shipment.shippedAt && !isCancelled
                ? shipDialog.open
                : undefined
            }
            onEditDeliveryConfirmation={
              props.shipment.deliveredAt && !isCancelled
                ? deliveryDialog.open
                : undefined
            }
            onEditReceiveConfirmation={
              isInbound && props.shipment.receivedAt && !isCancelled
                ? receiveDialog.open
                : undefined
            }
          />

          <ShipmentLocationInfo
            shipment={props.shipment}
            onDriftResolved={() => {
              void queryClient.invalidateQueries({
                queryKey: shipmentsQueryKey,
              });
              void queryClient.invalidateQueries({ queryKey: ['location'] });
            }}
          />

          <Box marginTop={2} paddingX={1}>
            <Divider sx={{ borderStyle: 'dashed', mb: 2 }} />
            <Stack alignItems="flex-end" spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Shipping cost
              </Typography>
              <Typography
                variant="body2"
                color={
                  props.shipment.shippingCost !== null
                    ? 'text.primary'
                    : 'text.secondary'
                }
              >
                {formatCurrencyValue(props.shipment.shippingCost)}
              </Typography>
            </Stack>
          </Box>
        </Paper>

        {/* Line items section; the quarantine tab joins it once serials are held */}
        <Stack
          direction="row"
          marginBottom={2}
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          marginX={1}
        >
          <HorizontalNavigationLink
            to="/shipments/$shipmentKey"
            params={{ shipmentKey: props.shipmentKey }}
            search={(prev) => ({ ...prev, tab: 'line-items' as const })}
            activeOptions={{ includeSearch: true }}
          >
            Line items
          </HorizontalNavigationLink>
          {hasHolds && (
            <HorizontalNavigationLink
              to="/shipments/$shipmentKey"
              params={{ shipmentKey: props.shipmentKey }}
              search={(prev) => ({ ...prev, tab: 'quarantine' as const })}
              activeOptions={{ includeSearch: true }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>Quarantine</span>
                {openHoldCount > 0 && (
                  <Warning color="warning" sx={{ fontSize: '1rem' }} />
                )}
              </Stack>
            </HorizontalNavigationLink>
          )}
        </Stack>
        <HardfinTabCardWrapper
          title={activeTab === 'quarantine' ? 'Quarantine' : 'Line items'}
        >
          {activeTab === 'quarantine' ? (
            <QuarantinePanel shipment={props.shipment} isInbound={isInbound} />
          ) : (
            <ShipmentDetailLineItemsTable
              shipment={props.shipment}
              isInbound={isInbound}
            />
          )}
        </HardfinTabCardWrapper>
      </DetailPageBody>

      {/* Dialogs - conditionally render based on shipment type */}
      {isInbound ? (
        <InboundShipmentDialog
          isOpen={editDialog.isOpen}
          onClose={editDialog.close}
          shipment={props.shipment}
        />
      ) : (
        <OutboundShipmentDialog
          isOpen={editDialog.isOpen}
          onClose={editDialog.close}
          shipment={props.shipment}
        />
      )}

      {!isInbound && (
        <OutboundShipDialog
          isOpen={shipDialog.isOpen}
          onClose={shipDialog.close}
          shipment={props.shipment}
        />
      )}

      <DeliveryDialog
        isOpen={deliveryDialog.isOpen}
        onClose={deliveryDialog.close}
        shipment={props.shipment}
      />

      {isInbound && (
        <ReceiveDialog
          isOpen={receiveDialog.isOpen}
          onClose={receiveDialog.close}
          shipment={props.shipment}
        />
      )}

      <ShipmentCancelDialog
        isOpen={cancelDialog.isOpen}
        onClose={cancelDialog.close}
        shipmentKey={props.shipmentKey}
        shipmentNumber={props.shipment.shipmentNumber}
        shippingCost={props.shipment.shippingCost}
        onSuccess={handleCancelSuccess}
      />
    </Container>
  );
}
